/**
 * One session's conversation (TMMAPP-0142 items 4, 7, 16, 17; §E 24): the transcript, the turn in
 * flight, and the opt-in cards. The transcript is the server's (`GET …/messages`), merged by id. It
 * lives only in memory — never in browser storage.
 *
 * - **Paged from the newest** (TMMAPP-0148 U2 items 11–13; TMMAPI-0184 U2): the session opens on
 *   its newest page (`?limit=`, no id); `loadOlder` pages back (`beforeId` = the first line held)
 *   while `hasMore` says older lines remain, and what's new is read only after the last line held
 *   (`?afterId=`, H3). Nothing reads a whole transcript. At most one of the three ids is ever sent.
 * - **A window around a line** (a search hit, `?message=`): `jump` reads `?aroundId=`. The view is
 *   then away from the newest (`window.around`): the live poll appends nothing, and anything that
 *   needs the newest (a send, a turn's answer, Back to the newest) reads the newest page again.
 *   The window serves `hasNewer` (TMMAPI-0185 U1): `loadNewer` reads on (`afterId` = the last
 *   line held) until no newer line remains; the view is then at the newest again, and the live poll
 *   and what's new resume from there.
 *
 * - **Asking the assistant** posts `{text, tier}` (a shared session adds `askAi: true`) and polls
 *   `GET …/turns/{turnId}` (about 1 s at first, backing off to 3 s) until `DONE`, `NEEDS_SCOPE`,
 *   `NEEDS_CONFIRMATION` or `FAILED`. One question at a time per session: a guard here as well as the server's
 *   `409 ASSISTANT_TURN_RUNNING`. The poll gives up after five failures in a row, or at once on a
 *   403 or `SUBSCRIPTION_EXPIRED`, and offers Retry (H1).
 * - **A message to the other members** (a shared session, `askAi: false`) is free and never held:
 *   humans can post while an answer runs.
 * - **Live updates** (a shared session): the transcript is polled every 4 s while the page is
 *   visible, paused while it's hidden, and read at once when it's shown or focused again. Every 5th
 *   tick re-reads the session view too (K9: another member's archive locks the composer). The poll
 *   stops at once on a 404 (the session is gone: `onSessionStale`), a 403 or
 *   `SUBSCRIPTION_EXPIRED`, and after five failures in a row (K7).
 * - **A ticket draft** (Help & feedback, `NEEDS_CONFIRMATION`): a final status. The answer joins the
 *   transcript, which carries the draft on its `ASSISTANT` line (`ticketDraft`, TMMAPI-0183 U1
 *   item 7a), so an unfiled draft survives a reload (L8); since TMMAPI-0185 U1 a draft always has
 *   its line, with empty text when the answer has none. The draft the turn served is held here too
 *   (`turnDrafts`) until the line lands. `rereadLine` reads one line again (and what follows it),
 *   for a draft "Not now" found already filed. The next message is never blocked by a draft.
 * - **The open turn** is the session view's `runningTurn` (TMMAPI-0182 U2, served on every session):
 *   on open, and whenever the view is re-read (a send whose answer was lost, M3), the caller's own
 *   (`mine`) is followed: a running one is polled, a waiting one's card is already in the transcript.
 *   Another member's turn is never followed, so its failure (and any wallet prompt) shows only to
 *   its asker; once it clears, what's new is read (`afterId`).
 * - **The opt-in card** is the transcript's `SCOPE_REQUEST` line (J2): Allow runs the caller's grant,
 *   Not now declines; either way the turn continues and the line's served `outcome` records it. A
 *   `PENDING` line is re-read (the read starts just before it) only when its outcome may have moved
 *   (TMMAPP-0148 E8): after the caller's own send or choice, when the session view's `runningTurn`
 *   changes (the K9 re-read), or when new lines arrive. The 4 s poll never rewinds on its own.
 * - Polling stops when the session changes or the page unmounts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  assistantApi,
  type AssistantMessage,
  type AssistantRunningTurn,
  type AssistantScopeRequest,
  type AssistantTicketDraft,
  type AssistantTier,
  type AssistantTurn,
} from '@/api/assistant';
import { ErrorCodes, isApiError } from '@/api/errors';

/** Delays between polls: about a second at first, backing off to three. */
export const TURN_POLL_DELAYS_MS = [1000, 1000, 1500, 2000, 3000] as const;
/** Consecutive poll failures before the turn's poll gives up (H1). */
export const TURN_POLL_MAX_FAILURES = 5;
/** A shared session's transcript poll (item 17). */
export const LIVE_POLL_MS = 4000;
/** Every this many live ticks, the session view is re-read too (K9). */
export const LIVE_SESSION_EVERY = 5;
/** Lines in a transcript page: the newest, each older one, and the window around a line. */
export const TRANSCRIPT_PAGE = 50;

const NO_NEWER: NewerLines = { hasNewer: false, loading: false, error: null, readTo: null };

function pollDelay(attempt: number): number {
  return TURN_POLL_DELAYS_MS[Math.min(attempt, TURN_POLL_DELAYS_MS.length - 1)]!;
}

/** A poll error that ends the poll at once: no access (403) or the subscription expired. */
function fatalPollError(error: unknown): boolean {
  return (
    isApiError(error) && (error.status === 403 || error.code === ErrorCodes.SUBSCRIPTION_EXPIRED)
  );
}

/** The transcript with `fresh` merged in: one row per id, oldest first. */
export function mergeMessages(
  current: readonly AssistantMessage[],
  fresh: readonly AssistantMessage[],
): AssistantMessage[] {
  const byId = new Map<number, AssistantMessage>();
  for (const message of current) byId.set(message.id, message);
  for (const message of fresh) byId.set(message.id, message);
  return [...byId.values()].sort((a, b) => a.id - b.id);
}

/** A scope request line still waiting for its asker's choice. */
function pendingScope(message: AssistantMessage): boolean {
  return message.role === 'SCOPE_REQUEST' && message.outcome === 'PENDING';
}

/** Allow or Not now on a scope request line (by its turn): only the choice in flight, never its outcome. */
export interface ScopeChoice {
  /** Allow or Not now is running. */
  busy: boolean;
  /** The last choice's failure; null when none. */
  error: unknown;
}

/** A support turn that drafted a ticket, as its turn served it (`NEEDS_CONFIRMATION`). */
export interface TurnDraft {
  turnId: number;
  draft: AssistantTicketDraft;
  /** The turn's words beside the draft; null when it had none. */
  answer: string | null;
}

export interface CurrentTurn {
  id: number;
  /** The last poll's answer; null until the first one. */
  data: AssistantTurn | null;
}

/** The turn's poll gave up (H1): its error, and the turn Retry polls again. */
export interface PollFailure {
  turnId: number;
  error: unknown;
}

/** The window of the transcript held: the newest (`around` null), or around a line. */
export interface TranscriptWindow {
  /** Each window read has its own key. */
  key: number;
  around: number | null;
}

/**
 * Paging on from a window around a line (TMMAPI-0185 U1): whether newer lines remain, the read in
 * flight or its failure, and the last line paging read (lines up to it are no arrival).
 */
export interface NewerLines {
  hasNewer: boolean;
  loading: boolean;
  error: unknown;
  readTo: number | null;
}

/** Paging back: whether older lines remain, and the read in flight or its failure. */
export interface OlderLines {
  hasMore: boolean;
  loading: boolean;
  error: unknown;
}

interface FetchNewOptions {
  /** Start just before the oldest `PENDING` scope line, so its outcome is read again (E8). */
  rewind?: boolean;
  /** The live poll (or a turn change seen there): away from the newest, nothing is read. */
  live?: boolean;
}

interface ConversationOptions {
  sessionId: number;
  /** Open on a window around this line (a search hit's `?message=`) instead of the newest. */
  aroundId?: number | null | undefined;
  /** A shared session: `askAi` is always sent, and the transcript is polled live. */
  shared?: boolean | undefined;
  /** Nothing new can be written (archived, access ended): no live polling. */
  readOnly?: boolean | undefined;
  /**
   * Re-read the session view: on every 5th live tick (K9), and when the live poll finds the session
   * gone (404, K7).
   */
  onSessionStale?: (() => void) | undefined;
  /** A turn ended (or stopped to ask): the page re-reads usage, the privacy panel and the wallet. */
  onTurnEnd: (turnId: number) => void;
  /** Allow: widen the session to what the card asks for (PATCH); throws on failure. */
  onAllowScope: (request: AssistantScopeRequest) => Promise<void>;
  /** The session view's open turn: the caller's own is followed. */
  runningTurn?: AssistantRunningTurn | null | undefined;
  /**
   * Messages arrived after the first load (a turn's answer, another member's message), with the
   * lines now held.
   */
  onArrived?:
    ((arrived: readonly AssistantMessage[], all: readonly AssistantMessage[]) => void) | undefined;
}

export interface Conversation {
  messages: AssistantMessage[] | null;
  loadError: unknown;
  reload: () => void;
  turn: CurrentTurn | null;
  /** An answer is being worked on: asking is held. */
  running: boolean;
  /** Allow / Not now in flight, by turn. */
  choices: Readonly<Record<number, ScopeChoice>>;
  /** Reads what's new in the transcript (after a send whose answer was lost, K3). */
  refresh: () => void;
  /**
   * Asks the assistant; resolves true when accepted and false, without a request, while a question
   * or its turn is in flight (one per session). A server refusal throws.
   */
  send: (text: string, tier: AssistantTier, attachmentIds?: readonly number[]) => Promise<boolean>;
  /** A shared session's message to the other members (free, no AI); never held. Throws on refusal. */
  post: (text: string, attachmentIds?: readonly number[]) => Promise<boolean>;
  allow: (turnId: number) => Promise<void>;
  decline: (turnId: number) => Promise<void>;
  /** Clears a failed turn's row (Try again re-sends the person's message). */
  dismissFailure: () => void;
  /** The turn's poll gave up; null while it runs or after Retry. */
  pollFailure: PollFailure | null;
  /** Support turns this view saw draft a ticket, by turn (the transcript's line carries the rest). */
  turnDrafts: Readonly<Record<number, TurnDraft>>;
  /** Polls the given-up turn again. */
  retryPoll: () => void;
  /** The window held; null until the first one loads. */
  window: TranscriptWindow | null;
  /**
   * Away from the newest, or asked to be (a read around a line in flight, or failed): Back to the
   * newest is offered.
   */
  awayRequested: boolean;
  older: OlderLines;
  /** Reads the page before the first line held (no-op while one is in flight or none remain). */
  loadOlder: () => void;
  newer: NewerLines;
  /** Reads the page after the last line held, while a window around a line has newer lines. */
  loadNewer: () => void;
  /** Reads this line again, and what follows it (its draft's state may have moved). */
  rereadLine: (messageId: number) => Promise<void>;
  /** Reads a window around this line (a search hit). */
  jump: (messageId: number) => void;
  /** Back to the newest page. */
  toNewest: () => void;
}

export function useConversation({
  sessionId,
  aroundId = null,
  shared = false,
  readOnly = false,
  onSessionStale,
  onTurnEnd,
  onAllowScope,
  onArrived,
  runningTurn = null,
}: ConversationOptions): Conversation {
  const [messages, setMessages] = useState<AssistantMessage[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [turn, setTurn] = useState<CurrentTurn | null>(null);
  const [pollTurnId, setPollTurnId] = useState<number | null>(null);
  const [pollKey, setPollKey] = useState(0);
  const [pollFailure, setPollFailure] = useState<PollFailure | null>(null);
  const [choices, setChoices] = useState<Record<number, ScopeChoice>>({});
  const [turnDrafts, setTurnDrafts] = useState<Record<number, TurnDraft>>({});
  const [reloadKey, setReloadKey] = useState(0);
  // The window to read: the newest, or around a line; a new key reads it again.
  const [view, setView] = useState<TranscriptWindow>(() => ({ key: 0, around: aroundId }));
  // The window held (set once its read lands).
  const [shown, setShown] = useState<TranscriptWindow | null>(null);
  const [older, setOlder] = useState<OlderLines>({ hasMore: false, loading: false, error: null });
  const [newer, setNewer] = useState<NewerLines>(NO_NEWER);
  // Each window read: a read of what's new or older begun under an earlier one is dropped.
  const generation = useRef(0);
  // The held window is around a line, not the newest: the live poll appends nothing.
  const away = useRef(false);
  const olderBusy = useRef(false);
  const newerBusy = useRef(false);
  // A question or its running turn is in flight: from the POST until the turn stops running.
  const inFlight = useRef(false);
  // The turn being polled, for reads outside a render.
  const polling = useRef<number | null>(null);
  // Turns this view saw stop (answered, failed, or waiting on a card): never resumed again.
  const stopped = useRef(new Set<number>());
  // The newest message id held, for `?afterId=`; null until the transcript loads.
  const lastId = useRef<number | null>(null);
  // The transcript as held, for reads outside a render.
  const held = useRef<AssistantMessage[]>([]);
  const onTurnEndRef = useRef(onTurnEnd);
  const onAllowRef = useRef(onAllowScope);
  const onArrivedRef = useRef(onArrived);
  const onStaleRef = useRef(onSessionStale);
  useEffect(() => {
    onTurnEndRef.current = onTurnEnd;
    onAllowRef.current = onAllowScope;
    onArrivedRef.current = onArrived;
    onStaleRef.current = onSessionStale;
  });
  useEffect(() => {
    polling.current = pollTurnId;
  }, [pollTurnId]);

  const toNewest = useCallback(
    () => setView((current) => ({ key: current.key + 1, around: null })),
    [],
  );
  const jump = useCallback(
    (messageId: number) => setView((current) => ({ key: current.key + 1, around: messageId })),
    [],
  );

  /**
   * Reads what's new after the last message held, and merges it in (H3). A `PENDING` scope line is
   * read again only when its outcome may have moved (E8): `rewind`, or when new lines arrived.
   * Away from the newest, the live poll reads nothing, and anything else reads the newest page.
   */
  const fetchNew = useCallback(
    async ({ rewind = false, live = false }: FetchNewOptions = {}) => {
      if (away.current) {
        if (!live) toNewest();
        return;
      }
      const run = generation.current;
      const after = lastId.current ?? 0;
      const waiting = held.current.filter(pendingScope).map((message) => message.id);
      const before = waiting.length > 0 ? Math.min(...waiting) - 1 : null;
      let fresh = await assistantApi.messagesAfter(
        sessionId,
        rewind && before !== null ? Math.min(after, before) : after,
      );
      if (run !== generation.current) return;
      if (!rewind && before !== null && fresh.length > 0) {
        // Something arrived while a scope request waits: its outcome may have moved with it.
        const again = await assistantApi.messagesAfter(sessionId, before);
        if (run !== generation.current) return;
        fresh = mergeMessages(fresh, again);
      }
      if (fresh.length === 0) return;
      const known = new Set(held.current.map((message) => message.id));
      const arrived = fresh.filter((message) => !known.has(message.id));
      const newest = fresh.reduce((max, message) => Math.max(max, message.id), after);
      lastId.current = Math.max(newest, lastId.current ?? 0);
      held.current = mergeMessages(held.current, fresh);
      setMessages(held.current);
      if (arrived.length > 0) onArrivedRef.current?.(arrived, held.current);
    },
    [sessionId, toNewest],
  );

  const settle = useCallback(
    (next: AssistantTurn) => {
      inFlight.current = false;
      stopped.current.add(next.id);
      if (next.status === 'NEEDS_CONFIRMATION' && next.ticketDraft !== null) {
        const draft = next.ticketDraft;
        setTurnDrafts((all) => ({
          ...all,
          [next.id]: { turnId: next.id, draft, answer: next.answer },
        }));
      }
      setTurn({ id: next.id, data: next });
      if (next.status !== 'FAILED') {
        // The answer (or the scope request line) joins the transcript before "Thinking…" goes.
        void fetchNew({ rewind: true })
          .catch(() => undefined)
          .finally(() => setTurn((current) => (current?.id === next.id ? null : current)));
      }
      onTurnEndRef.current(next.id);
    },
    [fetchNew],
  );

  // Read the window: the newest page, or the one around a line.
  useEffect(() => {
    let live = true;
    generation.current += 1;
    const run = generation.current;
    void (async () => {
      try {
        const page = await assistantApi.messages(
          sessionId,
          view.around === null ? undefined : { aroundId: view.around },
          TRANSCRIPT_PAGE,
        );
        if (!live || run !== generation.current) return;
        // The line isn't in this session's window (a stale or edited `?message=`): the newest instead.
        if (view.around !== null && !page.items.some((line) => line.id === view.around)) {
          toNewest();
          return;
        }
        const lines = mergeMessages([], page.items);
        // Around a line with nothing newer, the window already reaches the newest.
        away.current = view.around !== null && page.hasNewer;
        lastId.current = lines.at(-1)?.id ?? null;
        held.current = lines;
        setMessages(lines);
        setOlder({ hasMore: page.hasMore, loading: false, error: null });
        setNewer({ ...NO_NEWER, hasNewer: away.current });
        setShown(view);
        setLoadError(null);
      } catch (error) {
        if (live) setLoadError(error);
      }
    })();
    return () => {
      live = false;
    };
  }, [sessionId, reloadKey, view, toNewest]);

  const loadOlder = useCallback(async () => {
    const first = held.current[0];
    if (first === undefined || olderBusy.current) return;
    olderBusy.current = true;
    const run = generation.current;
    setOlder((current) => ({ ...current, loading: true, error: null }));
    try {
      const page = await assistantApi.messages(sessionId, { beforeId: first.id }, TRANSCRIPT_PAGE);
      if (run !== generation.current) return;
      held.current = mergeMessages(page.items, held.current);
      setMessages(held.current);
      setOlder({ hasMore: page.hasMore, loading: false, error: null });
    } catch (error) {
      if (run === generation.current)
        setOlder((current) => ({ ...current, loading: false, error }));
    } finally {
      olderBusy.current = false;
    }
  }, [sessionId]);

  const loadNewer = useCallback(async () => {
    const last = held.current.at(-1);
    if (last === undefined || newerBusy.current || !away.current) return;
    newerBusy.current = true;
    const run = generation.current;
    setNewer((current) => ({ ...current, loading: true, error: null }));
    try {
      const page = await assistantApi.messages(sessionId, { afterId: last.id }, TRANSCRIPT_PAGE);
      if (run !== generation.current) return;
      held.current = mergeMessages(held.current, page.items);
      const readTo = held.current.at(-1)?.id ?? last.id;
      lastId.current = Math.max(readTo, lastId.current ?? 0);
      // With `afterId`, `hasMore` says newer lines remain; none left: the newest is held.
      if (!page.hasMore) away.current = false;
      setMessages(held.current);
      setNewer({ hasNewer: page.hasMore, loading: false, error: null, readTo });
    } catch (error) {
      if (run === generation.current)
        setNewer((current) => ({ ...current, loading: false, error }));
    } finally {
      newerBusy.current = false;
    }
  }, [sessionId]);

  const rereadLine = useCallback(
    async (messageId: number) => {
      const run = generation.current;
      // The window around the line itself (TMMAPP-0149 L2): ids aren't contiguous.
      const page = await assistantApi.messages(sessionId, { aroundId: messageId }, TRANSCRIPT_PAGE);
      if (run !== generation.current) return;
      // Only lines already held are replaced: what's new is the poll's and the send's to read.
      const known = new Set(held.current.map((message) => message.id));
      held.current = mergeMessages(
        held.current,
        page.items.filter((message) => known.has(message.id)),
      );
      setMessages(held.current);
    },
    [sessionId],
  );

  // The session view's open turn: the caller's own is followed once the transcript is held; another
  // member's is shown by the page, and what's new is read once it clears.
  const loaded = messages !== null;
  const openTurnId = runningTurn?.turnId ?? null;
  const openMine = runningTurn?.mine === true;
  const othersTurn = useRef<number | null>(null);
  useEffect(() => {
    if (!loaded) return undefined;
    const previous = othersTurn.current;
    othersTurn.current = openTurnId !== null && !openMine ? openTurnId : null;
    // Another member's turn moved (the K9 re-read of the session view): what's new is read, and a
    // waiting scope line's outcome with it (E8).
    if (previous !== null && previous !== othersTurn.current)
      void fetchNew({ rewind: true, live: true }).catch(() => undefined);
    if (openTurnId === null || !openMine) return undefined;
    if (inFlight.current || polling.current === openTurnId || stopped.current.has(openTurnId)) {
      return undefined;
    }
    let live = true;
    let answered = false;
    inFlight.current = true;
    assistantApi.turn(sessionId, openTurnId).then(
      (resumed) => {
        answered = true;
        if (!live) return;
        if (resumed.status === 'RUNNING') {
          setTurn({ id: resumed.id, data: resumed });
          setPollTurnId(resumed.id);
        } else if (resumed.status === 'NEEDS_SCOPE') {
          // Its card is the transcript's line; nothing runs until a choice.
          inFlight.current = false;
          stopped.current.add(resumed.id);
        } else {
          settle(resumed);
        }
      },
      () => {
        answered = true;
        if (live) inFlight.current = false;
      },
    );
    return () => {
      live = false;
      // Left before the read answered: nothing is in flight for this view any more.
      if (!answered) inFlight.current = false;
    };
  }, [loaded, sessionId, openTurnId, openMine, settle, fetchNew]);

  // Poll the running turn until it ends; stops on a session change or unmount.
  useEffect(() => {
    if (pollTurnId === null) return undefined;
    let cancelled = false;
    let timer: number | undefined;
    let attempt = 0;
    let failures = 0;
    const schedule = () => {
      timer = window.setTimeout(() => void tick(), pollDelay(attempt));
      attempt += 1;
    };
    const tick = async () => {
      try {
        const next = await assistantApi.turn(sessionId, pollTurnId);
        if (cancelled) return;
        failures = 0;
        if (next.status === 'RUNNING') {
          setTurn({ id: next.id, data: next });
          schedule();
          return;
        }
        setPollTurnId(null);
        settle(next);
      } catch (error) {
        if (cancelled) return;
        if (isApiError(error) && error.status === 404) {
          inFlight.current = false;
          setPollTurnId(null);
          setTurn(null);
          return;
        }
        failures += 1;
        if (fatalPollError(error) || failures >= TURN_POLL_MAX_FAILURES) {
          // "Thinking…" ends; the error shows with Retry.
          inFlight.current = false;
          setPollTurnId(null);
          setTurn(null);
          setPollFailure({ turnId: pollTurnId, error });
          return;
        }
        schedule();
      }
    };
    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [sessionId, pollTurnId, pollKey, settle]);

  // A shared session's live transcript: every 4 s while visible; paused while hidden.
  useEffect(() => {
    if (!shared || readOnly || !loaded) return undefined;
    let cancelled = false;
    let timer: number | undefined;
    let paused = false;
    let ticks = 0;
    let failures = 0;
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void tick(), LIVE_POLL_MS);
    };
    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === 'hidden') {
        paused = true;
        return;
      }
      try {
        await fetchNew({ live: true });
        failures = 0;
        ticks += 1;
        // Another member's archive (or an ended share) shows without a send (K9).
        if (ticks % LIVE_SESSION_EVERY === 0) onStaleRef.current?.();
      } catch (error) {
        if (cancelled) return;
        // The session is gone, or this person can't read it any more: stop (K7).
        if (isApiError(error) && error.status === 404) {
          cancelled = true;
          onStaleRef.current?.();
          return;
        }
        failures += 1;
        if (fatalPollError(error) || failures >= TURN_POLL_MAX_FAILURES) {
          cancelled = true;
          return;
        }
      }
      if (!cancelled) schedule();
    };
    const resume = () => {
      if (!paused || document.visibilityState === 'hidden') return;
      paused = false;
      void tick();
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
    };
  }, [shared, readOnly, loaded, fetchNew]);

  const startPolling = useCallback((turnId: number) => {
    inFlight.current = true;
    stopped.current.delete(turnId);
    setPollFailure(null);
    setTurn({ id: turnId, data: null });
    setPollTurnId(turnId);
    setPollKey((key) => key + 1);
  }, []);

  const send = useCallback(
    async (text: string, tier: AssistantTier, attachmentIds: readonly number[] = []) => {
      if (inFlight.current) return false;
      inFlight.current = true;
      try {
        const sent = await assistantApi.send(
          sessionId,
          text,
          shared ? { tier, askAi: true, attachmentIds } : { tier, attachmentIds },
        );
        if (sent.turnId === null) inFlight.current = false;
        else startPolling(sent.turnId);
        // The new question, and a waiting scope request it superseded (served `SUPERSEDED`).
        void fetchNew({ rewind: true }).catch(() => undefined);
        return true;
      } catch (error) {
        inFlight.current = false;
        // Another answer is still running. In a private session it is the caller's own: follow it.
        // In a shared session it may be another member's: the session view is re-read, and its
        // `runningTurn` is followed only when it is the caller's (`mine`), so another member's
        // answer (or failure, and any wallet prompt) stays its asker's.
        if (isApiError(error) && error.code === ErrorCodes.ASSISTANT_TURN_RUNNING) {
          const backend = error.details.backend;
          const running =
            typeof backend === 'object' && backend !== null ? backend.turnId : undefined;
          if (shared) onStaleRef.current?.();
          else if (typeof running === 'number') startPolling(running);
        }
        throw error;
      }
    },
    [sessionId, shared, startPolling, fetchNew],
  );

  const post = useCallback(
    async (text: string, attachmentIds: readonly number[] = []) => {
      await assistantApi.send(sessionId, text, { tier: 'FAST', askAi: false, attachmentIds });
      void fetchNew().catch(() => undefined);
      return true;
    },
    [sessionId, fetchNew],
  );

  const choose = useCallback(
    async (turnId: number, choice: 'allow' | 'decline') => {
      const line = held.current.find(
        (message) => pendingScope(message) && message.turnId === turnId,
      );
      if (line === undefined || line.scopeRequest === null || choices[turnId]?.busy === true)
        return;
      const request = line.scopeRequest;
      setChoices((all) => ({ ...all, [turnId]: { busy: true, error: null } }));
      try {
        if (choice === 'allow') await onAllowRef.current(request);
        else await assistantApi.declineScope(sessionId, turnId);
        const resumed = await assistantApi.continueTurn(sessionId, turnId);
        startPolling(resumed);
        // The line's outcome (DECLINED, ACCEPTED, PARTIAL or NOT_CHANGED) is the server's.
        await fetchNew({ rewind: true }).catch(() => undefined);
        setChoices((all) => ({ ...all, [turnId]: { busy: false, error: null } }));
      } catch (error) {
        setChoices((all) => ({ ...all, [turnId]: { busy: false, error } }));
      }
    },
    [choices, sessionId, startPolling, fetchNew],
  );

  return {
    messages,
    loadError,
    reload: () => setReloadKey((key) => key + 1),
    turn,
    running: turn !== null && (turn.data === null || turn.data.status === 'RUNNING'),
    choices,
    refresh: () => void fetchNew().catch(() => undefined),
    send,
    post,
    allow: (turnId) => choose(turnId, 'allow'),
    decline: (turnId) => choose(turnId, 'decline'),
    dismissFailure: () => setTurn(null),
    pollFailure,
    turnDrafts,
    retryPoll: () => {
      if (pollFailure !== null) startPolling(pollFailure.turnId);
    },
    window: shown,
    awayRequested: view.around !== null || shown?.around != null,
    older,
    loadOlder: () => void loadOlder(),
    newer,
    loadNewer: () => void loadNewer(),
    rereadLine,
    jump,
    toNewest,
  };
}
